/**
 * Which Kontrol Edge routes a 'blocked' maintenance window replaces.
 *
 * Only the views whose content comes from telemetry. Everything else in the
 * product reads the database and stays correct during a window, so blocking
 * it would be theatre: Admin Center, the ordering flow and Account are all
 * reachable and all still true.
 *
 * Support is never in this list, and that is the point of the mode rather
 * than an exception to it. The one thing a customer may genuinely need while
 * their dashboard is dark is a way to reach a person.
 */
const TELEMETRY_ROUTE_PREFIXES = [
    "/kontrol/port",
    // The detail screens are siblings, not children: `/kontrol/port-details`
    // is its own route. Matching is per path segment so that `/kontrol-extra`
    // could never be swallowed by `/kontrol`, which means each sibling has to
    // be named rather than picked up by a prefix.
    "/kontrol/port-details",
    "/kontrol/ipx",
    "/kontrol/internet",
    "/kontrol/datacenter",
    "/kontrol/datacenter-details",
    "/kontrol/cloud-connectivity",
    "/kontrol/backbone",
    "/kontrol/backbone-details",
    "/kontrol/fwaas",
    "/kontrol/sdwan-hub",
    "/kontrol/sdwan-hub-details",
    "/kontrol/sdwan-branch-details",
    "/kontrol/extender-pop",
    "/kontrol/extender-pop-details",
    "/kontrol/extender-pop-cpe-details",
    "/kontrol/outages",
    "/kontrol/alerts",
    "/kontrol/internet-ip-monitoring",
    "/kontrol/reports",
];

/** The overview itself, which is `/kontrol` exactly rather than a prefix. */
const OVERVIEW_ROUTE = "/kontrol";

/**
 * Routes that live under a blocked prefix but are not telemetry. Checked
 * first, so adding a prefix above cannot silently swallow one of these.
 */
const EXEMPT_PREFIXES = ["/kontrol/admin-center"];

function normalize(pathname: string): string {
    const path = (pathname.split("?")[0] || "").trim();
    // Trailing slashes would defeat the exact match on the overview.
    return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

function matchesPrefix(path: string, prefix: string): boolean {
    return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Whether this route's content is telemetry, and therefore whether a blocked
 * window should replace it.
 */
export function isTelemetryRoute(pathname: string): boolean {
    const path = normalize(pathname);
    if (!path) return false;

    if (EXEMPT_PREFIXES.some((prefix) => matchesPrefix(path, prefix))) {
        return false;
    }

    if (path === OVERVIEW_ROUTE) return true;

    return TELEMETRY_ROUTE_PREFIXES.some((prefix) =>
        matchesPrefix(path, prefix)
    );
}

/**
 * Internal users are never blocked. The reason to run a window with the
 * dashboard dark is to spare customers a screen full of things we cannot
 * measure — not to blind the team that is running the maintenance.
 *
 * Note this buys them navigation, inventory and Admin Center, not telemetry:
 * while the collector is down there is no live data for anyone.
 */
export function isExemptFromBlock(tenantType?: string | null): boolean {
    return tenantType === "GLOBAL_ADMIN";
}
